import { CollectionHelpers } from 'src/helpers/CollectionHelpers';

import {
    BuildSupportMessageOptions,
    ChatUserRole,
    DepSupportChannelType,
    DiagramChatChannel,
    DiagramChatChannelCompany,
    DiagramChatChannelType,
    DiagramChatMessage,
    Team,
    User,
} from 'models';
import { TeamUserHelpers } from 'helpers/TeamUserHelpers';

type FormatConversationHistoryOptions = {
    messages: DiagramChatMessage[];
    userIds?: string[];
    getText: (content: DiagramChatMessage['content']) => string;
};

const defaults = {
    channelNames: {
        [DiagramChatChannelType.ENGINEERING_SUPPORT]: 'engineering-support',
        [DiagramChatChannelType.FEATURE_REQUEST]: 'feature-request',
    } satisfies Record<DepSupportChannelType, string>,
    messageLimit: 20,
};

const ChatHelpers = {
    defaults,

    depSupportChannelTypes: [
        DiagramChatChannelType.ENGINEERING_SUPPORT,
        DiagramChatChannelType.FEATURE_REQUEST,
    ] satisfies DepSupportChannelType[],

    swr: {
        channels: (projectId: string) => `project-chat-channels/${projectId}`,
        channelCollaborators: (projectId: string) => `project-chat-channel-collaborators/${projectId}`,
        channelCompanies: (projectId: string) => `project-chat-channel-companies/${projectId}`,
        channel: (channelId: string) => `project-chat-channel/${channelId}`,
        messages: (channelId: string, limit = defaults.messageLimit) => `project-chat-messages/${channelId}/${limit}`,
    },

    transformChannelName: (name: string) => {
        let transformed = name;

        transformed = transformed.replace(/\s/g, '-');
        transformed = transformed.replace(/[^a-zA-Z-]/g, '');
        transformed = transformed.toLowerCase();

        return transformed;
    },

    getChannelByType: (channels: DiagramChatChannel[], type: DiagramChatChannel['type']) => {
        return channels.find((channel) => channel.type === type);
    },

    isDepSupportChannel: (channel: { type?: DiagramChatChannel['type'] | string | null }) => {
        return ChatHelpers.depSupportChannelTypes.some((type) => type === channel.type);
    },

    isSupportChannel: (channel: { type?: DiagramChatChannel['type'] | string | null }) => {
        return (
            ChatHelpers.isDepSupportChannel(channel) || channel.type === DiagramChatChannelType.MANUFACTURING_SUPPORT
        );
    },

    isGeneralTeamChannel: (channel: Pick<DiagramChatChannel, 'type' | 'name'>) => {
        return channel.type === DiagramChatChannelType.TEAM && channel.name === 'general';
    },

    getChannelLabel: (channel: Pick<DiagramChatChannel, 'type' | 'name'>) => {
        return ChatHelpers.isGeneralTeamChannel(channel) ? 'Team Chat' : `#${channel.name}`;
    },

    findDefaultChannel: (channels: DiagramChatChannel[], type: DepSupportChannelType) => {
        const channelsOfType = channels.filter((channel) => channel.type === type);

        return (
            channelsOfType.find((channel) => channel.name === ChatHelpers.defaults.channelNames[type]) ??
            channelsOfType[0]
        );
    },

    /** The companies that have access to the channel. */
    companyIds: (channelId: string | null | undefined, companies: DiagramChatChannelCompany[]): string[] => {
        return companies.filter((row) => row.channel === channelId).map((row) => row.company);
    },

    /** The companies of the rows along with their subcontractors, as far as the user gets to see those. */
    memberCompanyIds: (channelCompanies: DiagramChatChannelCompany[]): string[] => {
        return [...new Set(channelCompanies.flatMap((row) => [row.company, ...(row.subcontractors ?? [])]))];
    },

    findCompanyChannel: (
        channels: DiagramChatChannel[],
        channelCompanies: DiagramChatChannelCompany[],
        companyIds: string[],
    ) => {
        const [channel] = channels.filter((channel) =>
            ChatHelpers.memberCompanyIds(channelCompanies.filter((row) => row.channel === channel.id)).some((id) =>
                companyIds.includes(id),
            ),
        );

        return channel;
    },

    resolveSupportTeamCompanyId: (channelCompanyId?: string | null, supportTeamId?: string | null): string | null =>
        channelCompanyId || supportTeamId || null,

    getParticipants: (channelUserIds: string[], team?: Team) => {
        const set = new Set<string>();

        channelUserIds.forEach((user) => {
            set.add(user);
        });

        CollectionHelpers.ids(TeamUserHelpers.fromTeam(team), 'user').forEach((userId) => {
            set.add(userId);
        });

        return Array.from(set.values());
    },

    buildSupportMessage: (options: BuildSupportMessageOptions) => {
        const { projectId, messageId, projectName, projectUrl, date, message, conversationHistory, channelType } =
            options;
        const projectNameLine = projectName ? `\nprojectName: "${projectName}"` : '';
        // Engineering support messages keep their original format.
        const channelTypeLine =
            channelType && channelType !== DiagramChatChannelType.ENGINEERING_SUPPORT
                ? `\nchannelType: ${channelType}`
                : '';

        const header = `# Support Request\nprojectId: ${projectId}\nmessageId: ${messageId}${projectNameLine}\nprojectUrl: "${projectUrl}"${channelTypeLine}\ndate: ${new Date(date).toISOString()}\nmessage: "${message}"`;
        const historySection = conversationHistory ? `\n\n# Conversation History\n${conversationHistory}\n` : '';
        return `${header}${historySection}`;
    },

    formatConversationHistory: ({ messages, userIds, getText }: FormatConversationHistoryOptions): string => {
        if (messages.length === 0) return '';

        const entries = messages
            .map((message) => {
                const dateText = message.createdAt ? `date: ${new Date(message.createdAt).toISOString()}\n` : '';
                const text = getText(message.content);
                const role = userIds?.length
                    ? CollectionHelpers.contains(userIds, message.createdBy)
                        ? ChatUserRole.USER
                        : ChatUserRole.EXPERT
                    : message.createdBy;
                return `${dateText}${role}: ${text}`;
            })
            .join('\n\n');
        return `\n${entries}`;
    },
};

export { ChatHelpers };
